import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, User, Lock, Mail } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { PASSWORD_POLICY_DESCRIPTION, passwordPolicyError } from "@/lib/passwordPolicy";

// =====================================================================
// Settings → Profile (current user)
//
// Edits the calling user's full_name + phone (no email change yet —
// that requires a verification email round-trip via supabase.auth and
// is queued for a future polish round). Lets the user change their
// password via supabase.auth.updateUser({ password, current_password }).
// =====================================================================

export default function SettingsProfile() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [profile, setProfile] = useState({ full_name: "", phone: "", email: "", created_at: null });

  const [pw, setPw]           = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy]   = useState(false);
  const [pwError, setPwError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("get_my_profile");
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load profile."); return; }
    setProfile({
      full_name:  data?.full_name  ?? "",
      phone:      data?.phone      ?? "",
      email:      data?.email      ?? "",
      created_at: data?.created_at ?? null,
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    if (!profile.full_name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError(null);
    const { error: e } = await supabase.rpc("update_my_profile", {
      p_input: { full_name: profile.full_name.trim(), phone: profile.phone || null },
    });
    setSaving(false);
    if (e) { setError(e.message ?? "Save failed."); return; }
    toast.success("Profile saved.");
  }

  async function handleChangePassword() {
    setPwError(null);
    const policyError = passwordPolicyError(pw);
    if (policyError)          { setPwError(policyError); return; }
    if (!currentPw)           { setPwError("Current password is required."); return; }
    if (pw !== pwConfirm)     { setPwError("Passwords don't match."); return; }
    setPwBusy(true);
    const { error: e } = await supabase.auth.updateUser({ password: pw, current_password: currentPw });
    setPwBusy(false);
    if (e) { setPwError(e.message ?? "Could not change password."); return; }
    toast.success("Password changed.");
    setCurrentPw(""); setPw(""); setPwConfirm("");
  }

  return (
    <div className="space-y-4" data-testid="settings-profile">
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <h1 className="text-lg font-semibold text-slate-900">Your profile</h1>
        <p className="text-xs text-slate-500">How your name appears in the team list, on activity logs, and as the sender on emails you trigger.</p>
      </div>

      {error ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
        </div>
      ) : (
        <>
          {/* Name + phone */}
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="profile-name">Full name</label>
              <input id="profile-name" value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                data-testid="profile-name"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="profile-phone">Phone</label>
              <input id="profile-phone" value={profile.phone} placeholder="Optional"
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                data-testid="profile-phone"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={handleSave} disabled={saving}
                data-testid="profile-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>

          {/* Email (read-only for now) */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Sign-in email</p>
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-700">
              <Mail className="w-4 h-4 text-slate-400" />{profile.email}
            </div>
            <p className="text-xs text-slate-400 mt-2">Email changes require a verification round-trip — coming in a later release. For now, contact support to change.</p>
          </div>

          {/* Password change */}
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Lock className="w-4 h-4" />Change password</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {PASSWORD_POLICY_DESCRIPTION} Current password is required.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input type="password" placeholder="Current password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                data-testid="profile-pw-current"
                autoComplete="current-password"
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
              <input type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)}
                data-testid="profile-pw-new"
                autoComplete="new-password"
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
              <input type="password" placeholder="Confirm new password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                data-testid="profile-pw-confirm"
                autoComplete="new-password"
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
            </div>
            {pwError ? <p data-testid="profile-pw-error" className="text-xs text-rose-700">{pwError}</p> : null}
            <div className="flex justify-end">
              <button type="button" onClick={handleChangePassword} disabled={pwBusy || !pw || !pwConfirm}
                data-testid="profile-pw-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
                {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Change password
              </button>
            </div>
          </div>

          {profile.created_at ? (
            <p className="text-xs text-slate-400 text-center">Account created {new Date(profile.created_at).toLocaleDateString()}</p>
          ) : null}
        </>
      )}
    </div>
  );
}
